import { useTranslate } from '@refinedev/core';
import { HeartOutlined } from '@ant-design/icons';
import type { PublicationListOut } from '@/api/generated/schemas';
import {
  MOBILE_PLACEHOLDER,
  MOBILE_SURFACE,
  GUTTER,
} from '@/components/mobile_preview/constants';
import { useMobilePreview } from '@/components/mobile_preview/mobilePreviewContext';
import {
  formatAudioDuration,
  publicationFormats,
} from '@/components/user_catalog/publications/publicationFacts';

/** Hero occupies most of the first screen in the app. */
const ASPECT = '3 / 4';
/** Over-cover copy stays white in both phone themes, as in the app. */
const ON_COVER_TEXT = '#FFFFFF';
/** Room for the fixed home header above and the page dots below. */
const HEADER_ROOM = 120;
const DOTS_ROOM = 34;

interface HomePreviewHeroSlideProps {
  publication?: PublicationListOut;
  /** Note replacing the copy block when there is nothing to show. */
  emptyNote?: string;
}

/** One hero entry as the phone draws it: full-bleed cover, title, and the
 * listen CTA the app hardcodes. */
export const HomePreviewHeroSlide = ({
  publication,
  emptyNote,
}: HomePreviewHeroSlideProps) => {
  const t = useTranslate();
  const { mobileTheme } = useMobilePreview();
  const surface = MOBILE_SURFACE[mobileTheme];

  const meta = [
    ...publicationFormats(publication).map((format) =>
      t(`user_catalog.common.formats.${format}`)
    ),
    formatAudioDuration(publication?.audio?.duration_minutes),
  ]
    .filter(Boolean)
    .join(' • ');

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        aspectRatio: ASPECT,
        // Every layer ends on the feed surface, so the fractional bottom edge
        // of a scaled frame has nothing darker left to antialias against.
        background: `linear-gradient(to top, ${surface} 0%, ${surface} 7%, transparent 14%), ${MOBILE_PLACEHOLDER[mobileTheme]}`,
        overflow: 'hidden',
      }}
    >
      {publication?.cover_url && (
        <img
          src={publication.cover_url}
          alt=""
          draggable={false}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      )}

      {/* Scrim carries the home header; the foot melts into the feed. The
          melt goes fully opaque a few percent early — landing on the surface
          exactly at the last row leaves a visible seam once scaled. */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `linear-gradient(to bottom, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.15) 45%, transparent 60%), linear-gradient(to top, ${surface} 0%, ${surface} 7%, rgba(0,0,0,0.45) 40%, transparent 70%)`,
        }}
      />

      <div
        style={{
          position: 'absolute',
          inset: `${HEADER_ROOM}px 0 ${DOTS_ROOM}px`,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 8,
          padding: `0 ${GUTTER}px`,
          color: ON_COVER_TEXT,
          textAlign: 'center',
        }}
      >
        {emptyNote ? (
          <div style={{ fontSize: 12, opacity: 0.75 }}>{emptyNote}</div>
        ) : (
          <>
            <div
              style={{
                fontFamily: 'Georgia, serif',
                fontSize: 22,
                lineHeight: 1.2,
                // A runaway title would climb into the header scrim.
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {publication?.title ?? ''}
            </div>
            {meta && <div style={{ fontSize: 12, opacity: 0.85 }}>{meta}</div>}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {/* The app's hero CTA is hardcoded to "listen". */}
              <div
                style={{
                  background: ON_COVER_TEXT,
                  color: '#111111',
                  borderRadius: 20,
                  padding: '7px 22px',
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                Słuchaj
              </div>
              <HeartOutlined style={{ fontSize: 20 }} />
            </div>
          </>
        )}
      </div>
    </div>
  );
};
